import { Controller } from "@hotwired/stimulus"
import tippy, { roundArrow, hideAll } from 'tippy.js'

// Reference count for tippy instances using the scroll listener
let scrollListenerCount = 0

// Handler for main document scroll - only hides tooltips on main page scroll.
// Hide instantly: animating out while the anchor scrolls away reads as a
// size/position glitch.
const handleMainScroll = () => {
  hideAll({ duration: 0 })
}

// aria-expanded is invalid on a non-interactive wrapper (axe aria-allowed-attr),
// and a portal dropdown's reference is exactly that.
const INTERACTIVE = '[aria-haspopup], button, [role="button"], a[href]'

const disclosureControl = (element) =>
  element.matches(INTERACTIVE) ? element : element.querySelector(INTERACTIVE)

// Check if an element's text is truncated
const isTruncated = (element) => {
  return element.scrollWidth > element.clientWidth
}

// Width of the widest visual line of text within an element. Every text rect is
// grouped into a line by vertical overlap, then we take the widest line's full
// horizontal span — using the span (not the widest single rect) so multi-column
// text is measured as a whole line rather than one cell.
const widestLine = (root) => {
  const rects = []
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT)
  while (walker.nextNode()) {
    const node = walker.currentNode
    if (!node.textContent.trim()) continue
    const range = document.createRange()
    range.selectNodeContents(node)
    rects.push(...range.getClientRects())
  }
  if (rects.length === 0) return 0

  const lines = []
  for (const rect of rects) {
    const line = lines.find((l) => rect.top < l.bottom && rect.bottom > l.top)
    if (line) {
      line.left = Math.min(line.left, rect.left)
      line.right = Math.max(line.right, rect.right)
      line.top = Math.min(line.top, rect.top)
      line.bottom = Math.max(line.bottom, rect.bottom)
    } else {
      lines.push({ left: rect.left, right: rect.right, top: rect.top, bottom: rect.bottom })
    }
  }
  return Math.max(...lines.map((l) => l.right - l.left))
}

export default class extends Controller {
  static targets = ['origin', 'template', 'truncate']

  connect() {
    // data-tippy-lazy: skip building the instance (popper DOM, listeners)
    // until the pointer first reaches the origin — the calendar renders
    // hundreds of tooltip carriers per frame and shows almost none of them.
    if ('tippyLazy' in this.#origin.dataset) {
      this.#armLazy()
    } else {
      this.#activate()
    }
  }

  #activate() {
    this.active = true
    this.#createInstance()

    // Turbo morph preserves the origin but drops the popper (appended to
    // document.body at runtime), so connect() doesn't re-run — rebuild here.
    document.addEventListener('turbo:morph', this.#handleMorph)

    // Hide tooltips on page scroll only (not container scroll), via one
    // reference-counted listener shared across all tippy instances.
    if (scrollListenerCount === 0) {
      window.addEventListener('scroll', handleMainScroll)
    }
    scrollListenerCount++
  }

  #armLazy() {
    this.lazyActivate = (event) => {
      this.#disarmLazy()
      this.#activate()
      // Replay the trigger so tippy's own listener runs it, delay included.
      // tippy registers 'focus', not 'focusin', so translate on the way in.
      this.#origin.dispatchEvent(new Event(event.type === 'focusin' ? 'focus' : event.type))
    }
    this.#origin.addEventListener('mouseenter', this.lazyActivate)
    this.#origin.addEventListener('focusin', this.lazyActivate)
  }

  #disarmLazy() {
    if (!this.lazyActivate) return

    this.#origin.removeEventListener('mouseenter', this.lazyActivate)
    this.#origin.removeEventListener('focusin', this.lazyActivate)
    this.lazyActivate = null
  }

  get #origin() {
    return this.hasOriginTarget ? this.originTarget : this.element
  }

  disconnect() {
    this.#disarmLazy()
    if (!this.active) return

    this.#collapse()

    // A popup held through its cell's redraw outlives this scope: its position
    // freezes (the reference is going away) and it destroys itself on close.
    // Popper's destroy strips the placement styles, so they are pinned back on.
    if (this.instance?.popper?.hasAttribute('data-hold-through-redraw')) {
      const popper = this.instance.popper
      // Pin the visual position, not the popper styles: depending on placement
      // popper anchors via left- or right-based inset, and destroy strips both.
      const { left, top } = popper.getBoundingClientRect()
      const { zIndex } = getComputedStyle(popper)
      this.instance.popperInstance?.destroy()
      Object.assign(popper.style, {
        position: 'fixed', margin: '0', transform: 'none', zIndex,
        left: `${left}px`, top: `${top}px`, right: 'auto', bottom: 'auto'
      })
    } else {
      this.instance?.destroy()
    }
    document.removeEventListener('turbo:morph', this.#handleMorph)

    scrollListenerCount--
    if (scrollListenerCount === 0) {
      window.removeEventListener('scroll', handleMainScroll)
    }

    // Stimulus reuses the controller across detach/re-attach cycles: without
    // resetting, a second disconnect would tear down again and underflow the
    // shared listener refcount.
    this.active = false
    this.instance = null
  }

  #handleMorph = () => {
    if (this.instance && !document.body.contains(this.instance.popper)) {
      this.#collapse()
      this.instance.destroy()
      this.#createInstance()
    }
  }

  // destroy() unmounts without ever running onHide.
  #collapse() {
    this.disclosure?.setAttribute('aria-expanded', 'false')
  }

  #createInstance() {
    const origin = this.#origin

    const {
      tippyContent,
      tippyPlacement,
      tippyArrow,
      tippyTrigger,
      tippyWidth,
      tippyWrapperClass,
      tippyDelay,
      tippyOffset,
      tippyTheme,
      tippyOnlyTruncated,
      tippyOnlyDisabled,
      tippyCenter,
      tippySelfSizing,
      tippyHoldThroughRedraw
    } = origin.dataset

    // data-tippy-self-sizing: no max-width cap, no hug-to-text resize.
    const selfSizing = tippySelfSizing !== undefined

    // data-tippy-only-truncated: only show tooltip when text overflows (scrollWidth > clientWidth)
    const onlyWhenTruncated = tippyOnlyTruncated !== undefined

    // data-tippy-only-disabled: only show tooltip while the wrapped control is disabled
    const onlyWhenDisabled = tippyOnlyDisabled !== undefined

    this.disclosure = tippyTrigger === 'click' ? disclosureControl(origin) : null

    // tippy auto-reads data-tippy-offset as a bare number, which overrides our [0, N]
    // config (Popper ignores a bare offset). Drop it so the config wins.
    if (tippyOffset !== undefined) origin.removeAttribute("data-tippy-offset")

    if (tippyContent || this.hasTemplateTarget) {
      this.instance = tippy(origin, {
        content: tippyContent || this.templateTarget.innerHTML,
        allowHTML: true,
        placement: tippyPlacement || 'bottom-start',
        arrow: tippyArrow ? roundArrow : false,
        theme: tippyTheme || 'light',
        maxWidth: selfSizing ? 'none' : (parseInt(tippyWidth) || undefined),
        trigger: tippyTrigger || undefined,
        // Default, not a law: text tooltips must not be interactive — their
        // popper would become the hover target when it appears under the
        // pointer, and surfacing under a pressed pointer resets the stream
        // mid-drag (buttons read 0, the drag dies). Template popups (menus,
        // popovers) need the hover.
        interactive: !tippyContent,
        // aria-expanded is ours: two writers race on listener order.
        aria: { content: 'auto', expanded: false },
        // Nesting inside the parent popper turns on tippy's stacked handling:
        // a press in the child is not "outside" the parent, and the parent
        // takes the child down when it hides.
        appendTo: () => origin.closest('[data-tippy-root]') ?? document.body,
        delay: parseInt(tippyDelay) || undefined,
        // data-tippy-center overlays the popper on its reference, lifted by
        // the given pixels.
        offset: tippyCenter !== undefined
          ? ({ reference, popper }) => [ 0, -((reference.height + popper.height) / 2 + (parseInt(tippyCenter) || 0)) ]
          : (tippyOffset !== undefined ? [ 0, parseInt(tippyOffset) ] : undefined),
        onCreate: (instance) => {
          if (tippyWrapperClass) instance.popper.classList.add(tippyWrapperClass)
          // Marks non-interactive tooltips so CSS can address them (hidden
          // during drags).
          if (!instance.props.interactive) instance.popper.classList.add('tippy-text')
          // data-tippy-hold-through-redraw: the popup survives its origin's
          // redraw (see disconnect) so it stays open across frame reloads.
          if (tippyHoldThroughRedraw !== undefined) instance.popper.setAttribute('data-hold-through-redraw', '')
        },
        onHidden: (instance) => {
          if (!instance.reference.isConnected) instance.destroy()
        },
        onShow: (instance) => {
          if (onlyWhenTruncated) {
            // Check the truncate target if available, otherwise check origin
            const truncateEl = this.hasTruncateTarget ? this.truncateTarget : instance.reference
            if (!isTruncated(truncateEl)) {
              return false // Cancel showing tooltip
            }
          }
          if (onlyWhenDisabled && !instance.reference.querySelector('[disabled], [aria-disabled="true"]')) {
            return false
          }

          this.disclosure?.setAttribute('aria-expanded', 'true')
        },
        onHide: () => {
          this.disclosure?.setAttribute('aria-expanded', 'false')
        },
        onMount: (instance) => {
          // Resize on mount (before the show transition paints) rather than
          // onShown, so the box never flashes at max-width and then shrinks.
          // Dropdowns and self-sizing popups manage their own width.
          if (tippyWrapperClass === 'tippy-dropdown' || selfSizing) return

          const box = instance.popper.querySelector('.tippy-box')
          const content = instance.popper.querySelector('.tippy-content')
          if (!box || !content) return

          // The configured max-width — the cap that wrappable text obeys. Read
          // it from tippy's resolved props, not the live box style: tippy reuses
          // the same box across hovers, so the style still holds the previous
          // hugged width and the cap would drift smaller on every re-hover.
          const cap = instance.props.maxWidth

          // Measure the content's intrinsic (unwrapped) size, then put the cap
          // back and see whether the content had to wrap to fit it.
          box.style.maxWidth = 'none'
          box.style.width = 'max-content'
          const intrinsicWidth = box.getBoundingClientRect().width
          const intrinsicHeight = content.getBoundingClientRect().height
          box.style.width = ''
          box.style.maxWidth = Number.isFinite(cap) ? `${cap}px` : 'none'
          const wraps = content.getBoundingClientRect().height > intrinsicHeight + 0.5

          if (wraps) {
            // Wrappable text obeys the cap but text-wrap: balance leaves
            // whitespace to the right, so hug the widest rendered line.
            const widest = widestLine(content)
            if (widest > 0) {
              const contentStyles = getComputedStyle(content)
              const padding = parseFloat(contentStyles.paddingLeft) + parseFloat(contentStyles.paddingRight)
              const boxStyles = getComputedStyle(box)
              const border = parseFloat(boxStyles.borderLeftWidth) + parseFloat(boxStyles.borderRightWidth)
              // +1 guards against sub-pixel rounding that would re-wrap a line
              box.style.maxWidth = `${Math.ceil(widest + padding + border) + 1}px`
            }
          } else {
            // Non-wrapping content (icons, columns) would be clipped — and show
            // a scrollbar — at the cap, so size the box to its intrinsic width.
            box.style.maxWidth = `${Math.ceil(intrinsicWidth) + 1}px`
          }
          // Re-position so the box stays centered on the reference after resize.
          instance.popperInstance?.update()
        }
      })
    }
  }
}
